import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  NON_CONFIGURABLE_INTEGRATIONS,
  IntegrationType,
  SENSORS_TYPE,
  INTEGRATION_TO_FEATURE_MAP,
} from '../../types/integration-type';
import { IntegrationTypeCardWebComponent } from '../../integrationTypeCard';
import { SendFeedbackWebComponent } from '../../sendFeedbackEmail';
import { toggleVisibility } from '../../../utils/htmlElement.utils';
import { LicenseProductIdEnum, PRODUCTS_WITH_EDR } from '@nirvana/shared/license-helper';
import { LicenseStatus } from '../../../license/types/LicenseStatus';

export class SelectIntegrationTypeWebView extends BaseWebComponentView {
  private integrationToLicenseMapping = {
    [IntegrationType.AZURE_CW]: [LicenseProductIdEnum.ProductIdXdrCloudSensors, LicenseProductIdEnum.ProductIdCws],
    [IntegrationType.AWS]: [LicenseProductIdEnum.ProductIdXdrCloudSensors, LicenseProductIdEnum.ProductIdCws],
    [IntegrationType.O365]: [LicenseProductIdEnum.ProductIdXdrProductivitySensors, LicenseProductIdEnum.ProductIdXdrBundle],
    [IntegrationType.AD_ONPREM]: [LicenseProductIdEnum.ProductIdXdrIdentitySensors, LicenseProductIdEnum.ProductIdXdrBundle],
    [IntegrationType.AZURE_AD]: [LicenseProductIdEnum.ProductIdXdrIdentitySensors, LicenseProductIdEnum.ProductIdXdrBundle],
    [IntegrationType.NSVA]: [LicenseProductIdEnum.ProductIdXdrNetworkSensors, LicenseProductIdEnum.ProductIdXdrBundle],
    [IntegrationType.INTUNE]: [LicenseProductIdEnum.ProductIdXdrIdentitySensors, LicenseProductIdEnum.ProductIdXdrBundle],
    [IntegrationType.GOOGLE_WS]: [LicenseProductIdEnum.ProductIdXdrProductivitySensors, LicenseProductIdEnum.ProductIdXdrBundle],
    [IntegrationType.GCP]: [LicenseProductIdEnum.ProductIdXdrCloudSensors, LicenseProductIdEnum.ProductIdCws],
    [IntegrationType.MOBILE_SECURITY]: this.getActiveStatusMobileSecuritySensor,
    [IntegrationType.CLOUD_SECURITY]: LicenseProductIdEnum.ProductIdCws,
    [IntegrationType.ATLASSIAN_CLOUD]: LicenseProductIdEnum.ProductIdXdrSensitiveDataSensors,
  };

  public dom: {
    $azureCwCard: IntegrationTypeCardWebComponent;
    $o365Card: IntegrationTypeCardWebComponent;
    $adOnPremCard: IntegrationTypeCardWebComponent;
    $awsCard: IntegrationTypeCardWebComponent;
    $azureAdCard: IntegrationTypeCardWebComponent;
    $nsvaCard: IntegrationTypeCardWebComponent;
    $intuneCard: IntegrationTypeCardWebComponent;
    $googleWSCard: IntegrationTypeCardWebComponent;
    $gcpCard: IntegrationTypeCardWebComponent;
    $mobileSecurityCard: IntegrationTypeCardWebComponent;
    $cloudSecurityCard: IntegrationTypeCardWebComponent;
    $atlassianCard: IntegrationTypeCardWebComponent;
    $feedbackCard: SendFeedbackWebComponent;
  };

  getDomElements() {
    this.dom = {
      $azureCwCard: this.shadow.querySelector('.gz-azure-cw-card'),
      $o365Card: this.shadow.querySelector('.gz-o365-card'),
      $adOnPremCard: this.shadow.querySelector('.gz-ad-onprem-card'),
      $awsCard: this.shadow.querySelector('.gz-aws-card'),
      $azureAdCard: this.shadow.querySelector('.gz-azure-ad-card'),
      $nsvaCard: this.shadow.querySelector('.gz-nsva-card'),
      $intuneCard: this.shadow.querySelector('.gz-intune-card'),
      $googleWSCard: this.shadow.querySelector('.gz-google-workspace-card'),
      $gcpCard: this.shadow.querySelector('.gz-gcp-card'),
      $mobileSecurityCard: this.shadow.querySelector('.gz-mobile-security-card'),
      $cloudSecurityCard: this.shadow.querySelector('.gz-cloud-security-card'),
      $atlassianCard: this.shadow.querySelector('.gz-atlassian-card'),
      $feedbackCard: this.shadow.querySelector('gz-feedback-card'),
    };
    this.getTranslations();
    if (this.dom.$feedbackCard.injectDependencies) {
      this.dom.$feedbackCard.injectDependencies(this.dependencies);
      this.dom.$feedbackCard.start();
    }
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getSensorsIntegrationsStrings();
    return this.translations;
  }

  buildSuccessView(data): void {
    this.initCard(this.dom.$azureCwCard, IntegrationType.AZURE_CW, data.licenses);
    this.initCard(this.dom.$o365Card, IntegrationType.O365, data.licenses);
    this.initCard(this.dom.$awsCard, IntegrationType.AWS, data.licenses);
    this.initCard(this.dom.$adOnPremCard, IntegrationType.AD_ONPREM, data.licenses);
    this.initCard(this.dom.$azureAdCard, IntegrationType.AZURE_AD, data.licenses);
    this.initCard(this.dom.$nsvaCard, IntegrationType.NSVA, data.licenses);
    this.initCard(this.dom.$intuneCard, IntegrationType.INTUNE, data.licenses);
    this.initCard(this.dom.$googleWSCard, IntegrationType.GOOGLE_WS, data.licenses);
    this.initCard(this.dom.$gcpCard, IntegrationType.GCP, data.licenses);
    this.initCard(this.dom.$mobileSecurityCard, IntegrationType.MOBILE_SECURITY, data.licenses);
    this.initCard(this.dom.$cloudSecurityCard, IntegrationType.CLOUD_SECURITY, data.licenses);
    this.initCard(this.dom.$atlassianCard, IntegrationType.ATLASSIAN_CLOUD, data.licenses);
  }

  getActiveStatusForCard(type, licenses) {
    if (typeof this.integrationToLicenseMapping[type] === 'function') {
      return this.integrationToLicenseMapping[type](type, licenses);
    }

    let element;
    if (Array.isArray(this.integrationToLicenseMapping[type])) {
      element = licenses.find(license => this.integrationToLicenseMapping[type].includes(license.productId));
    } else {
      element = licenses.find(license => license.productId === this.integrationToLicenseMapping[type]);
    }

    return element
      ? {
          expiryDate: element.expiryDate,
          status: element.status,
          _id: element._id,
        }
      : null;
  }

  getActiveStatusMobileSecuritySensor(type, licenses) {
    const mobileAddOnLicense = licenses.find(
      license => license.productId === LicenseProductIdEnum.ProductIdMobileSecurity
    );
    let element;
    for (const productId of PRODUCTS_WITH_EDR) {
      element = licenses.find(license => productId === license.productId);
      if (element) {
        break;
      }
    }

    if (mobileAddOnLicense && element && element.status === LicenseStatus.ACTIVE) {
      return {
        expiryDate: mobileAddOnLicense.expiryDate,
        status: mobileAddOnLicense.status,
        _id: mobileAddOnLicense._id,
      };
    }

    return null;
  }
  initCard(card, integrationType, licenses) {
    const hasPlatformFlag = this.dependencies.Ext.isFeatureAvailableForPlatform(
      INTEGRATION_TO_FEATURE_MAP[integrationType]
    );
    toggleVisibility(card, hasPlatformFlag);
    if (hasPlatformFlag) {
      card.injectDependencies(this.dependencies);
      card.license = this.getActiveStatusForCard(integrationType, licenses);
      card.type = integrationType;
      card.nonUserConfigurable = !!NON_CONFIGURABLE_INTEGRATIONS.find(type => type === integrationType);
      card.description = this.translations.integrationDescription[integrationType];
      card.integrationType = this.translations[SENSORS_TYPE[integrationType]];
      card.name = this.stringsTranslator.getTranslatedIntegrationNameByType(integrationType);
      card.start().then().catch(console.error);
      card.controller.render();
    }
  }
}
